import api_base, { serializeParams } from '@/api/base';
import { Case, caseSchema, Person } from '@/views/cases/data/schema';
import { CaseNoteFileTableParams } from '@/views/cases/page/table';
import { ClientsTableParams } from '@/views/clients';
// Analysis DTO for case files
export interface CaseFileAnalysisDto {
  success: boolean;
  analysis?: string | null;
  error?: string | null;
  text_length?: number | null;
  analysis_generated_at?: string | null;
}

export interface CaseListingParams {
  page: number;
  page_size: number;
  ordering: string | null;
  name?: string | null;
  types?: string[] | null;
  assigned_to?: string | null;
  client_id?: string | null;
  status?: string[] | null;
  is_favorite?: boolean | null;
}

interface CaseList {
  next: number | null
  previous: number | null
  results: Case[],
  count: number
}

export const get_cases = async (params: CaseListingParams | null = null): Promise<CaseList> => {
  const response = await api_base.get(
    '/api/v1/case/list?include_type_object',
    {
      params: { ...params },
      paramsSerializer: params => serializeParams(params)
    }
  )
  return response.data;
};

export interface AddCase {
  name: string
  client: string
  year: number | null
  number: number | null
  code: number | null
  assigned_to: string | null
  location: number | null
  service_fee: string | null
  fee_type: 'fixed' | 'percentage' | null
  fee_reference_amount: string | null
  type: string | null
  status: string | null
  visibility: 'public' | 'partial' | 'private'
  parent_case_id?: number
  relation_type?: string
}

export const add_case = async (body: Partial<AddCase>): Promise<void> => {
  await api_base.post(
    '/api/v1/case/create?include_type_object',
    body,
  );
};

export const update_case = async (id: number, body: Partial<AddCase>): Promise<Case> => {
  const response = await api_base.patch(
    '/api/v1/case/update/' + id + "?include_type_object",
    body,
  );
  const parsedResponse = caseSchema.parse(response.data);
  return parsedResponse;
};

export const remove_case = async (id: number): Promise<void> => {
  await api_base.delete(
    '/api/v1/case/remove/' + id + "?include_type_object"
  )
}

interface CaseLocationList {
  next: number | null
  previous: number | null
  results: CaseLocation[],
  count: number
}

interface CaseLocation {
  id: number
  name: string
  ville_id?: number | null
  ville_name?: string | null
}

export const get_case_locations = async (params: ClientsTableParams | null = null): Promise<CaseLocationList> => {
  const response = await api_base.get(
    '/api/v1/case/location/list',
    {
      params: { ...params },
      paramsSerializer: params => serializeParams(params)
    }
  )
  return response.data;
};

export interface CaseNote {
  id: number;
  added_by: Person;
  content: string;
  created_at: number;
  updated_at: number;
}

interface CaseNoteList {
  next: number | null;
  previous: number | null;
  results: CaseNote[];
  count: number;
}

export const get_case_notes =
  async (case_id: number, params: CaseNoteFileTableParams | null = null): Promise<CaseNoteList> => {
  const response = await api_base.get(
    '/api/v1/case/' + case_id + '/note/list',
    {
      params: { ...params },
      paramsSerializer: params => serializeParams(params)
    }
  );
  return response.data;
};

interface AddCaseNote {
  case: number;
  content: string;
}

export const add_case_note = async (body: AddCaseNote): Promise<void> => {
  await api_base.post(
    '/api/v1/case/note/create',
    body
  );
};

interface UpdateCaseNote {
  content: string;
}

export const update_case_note = async (note_id: number, body: UpdateCaseNote): Promise<void> => {
  await api_base.patch(
    `/api/v1/case/note/update/${note_id}`,
    body
  );
};

export const delete_case_note = async (note_id: number): Promise<void> => {
  await api_base.delete(
    `/api/v1/case/note/remove/${note_id}`
  );
};

export interface CaseFile {
  id: string;
  case: number;
  filename: string;
  original_filename: string;
  file_size: number;
  content_type: string;
  uploaded_by: Person;
  created_at: number;
  file_path: string;
  is_decision_file?: boolean;
  analysis_generated_at?: string | null;
}

interface CaseFileList {
  next: number | null;
  previous: number | null;
  results: CaseFile[];
  count: number;
}

export const get_case_files = 
  async (case_id: number, params: CaseNoteFileTableParams | null = null): Promise<CaseFileList> => {
  const response = await api_base.get(
    `/api/v1/case/${case_id}/file/list`,
    {
      params: { ...params },
      paramsSerializer: params => serializeParams(params)
    }
  );
  return response.data;
};

export const add_case_file = async (body: { case: number; file: File; is_decision_file?: boolean }): Promise<void> => {
  // Step 1: Get S3 upload credentials
  const credentialsResponse = await api_base.post(
    '/api/v1/case/file/upload-credentials',
    {
      case: body.case,
      filename: body.file.name,
      content_type: body.file.type,
      file_size: body.file.size
    }
  );
  
  const uploadData = credentialsResponse.data;
  
  const formData = new FormData();
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Object.entries(uploadData.upload_credentials.fields).forEach(([key, value]: [string, any]) => {
    formData.append(key, value);
  });

  formData.append('file', body.file);
  
  const resp = await fetch(uploadData.upload_credentials.url, {
    method: 'POST',
    body: formData
  });

  if (resp.status !== 204) {
    throw new Error('Failed to upload file to S3');
  }

  await api_base.post('/api/v1/case/file/create', {
    case: body.case,
    filename: uploadData.filename,
    original_filename: uploadData.original_filename || body.file.name,
    file_size: body.file.size,
    content_type: body.file.type,
    file_path: uploadData.file_path,
    is_decision_file: body.is_decision_file || false
  });
};

export const get_case_file_download_link = async (file_id: string): Promise<string> => {
  const response = await api_base.get(
    `/api/v1/case/file/${file_id}/url`
  );
  return response.data.file_url;
}

export const get_case_file_analysis = async (file_id: string): Promise<CaseFileAnalysisDto> => {
  const response = await api_base.get(`/api/v1/case/file/${file_id}/analysis`);
  return response.data;
};

export const post_case_file_analysis = async (
  file_id: string,
  data: { user_prompt?: string | null; force?: boolean }
): Promise<CaseFileAnalysisDto> => {
  const response = await api_base.post(`/api/v1/case/file/${file_id}/analysis`, data);
  return response.data;
};

export interface CaseProceeding {
  id: string
  decision: string
  type: string
  next_hearing_at: string | null
  created_at: number
  personal_decisions: PersonalDecision[]
}

export interface PersonalDecision {
  id: string
  decision: string
  created_at: number
  updated_at: number
  added_by: Person
}

interface CaseProceedingList {
  next: number | null;
  previous: number | null;
  results: CaseProceeding[];
  count: number;
}

export const get_case_proceedings = async (case_id: number): Promise<CaseProceeding[]> => {
  const response = await api_base.get<CaseProceedingList>(
    `/api/v1/case/${case_id}/proceeding/list`
  );
  return response.data.results;
}

interface ModPersonalDecisionBody {
  decision: string;
}

export const add_personal_decision = async (proceeding_id: string, body: ModPersonalDecisionBody): Promise<PersonalDecision> => {
  const response = await api_base.post(
    `/api/v1/case/proceeding/${proceeding_id}/decision/create`,
    body,
  );
  return response.data;
};

export const update_personal_decision = async (decision_id: string, body: ModPersonalDecisionBody): Promise<PersonalDecision> => {
  const response = await api_base.patch(
    `/api/v1/case/proceeding/decision/${decision_id}/update`,
    body,
  );
  return response.data;
};

export const remove_personal_decision = async (decision_id: string): Promise<void> => {
  await api_base.delete(
    `/api/v1/case/proceeding/decision/${decision_id}/remove`
  );
};

export const refresh_proceedings = async (case_id: number): Promise<void> => {
  await api_base.get(
    `/api/v1/case/${case_id}/proceeding/refresh`
  );
};

export const get_case_by_id = async (id: number): Promise<Case> => {
  const response = await api_base.get(`/api/v1/case/${id}?include_type_object=1`);
  const parsedResponse = caseSchema.parse(response.data);
  return parsedResponse;
};

export const get_case_decision_file = async (case_id: number): Promise<CaseFile> => {
  const response = await api_base.get(`/api/v1/case/${case_id}/decision-file`);
  return response.data;
};

export const delete_case_decision_file = async (case_id: number): Promise<void> => {
  await api_base.delete(`/api/v1/case/${case_id}/decision-file/delete`);
};

export const delete_case_file = async (file_id: string): Promise<void> => {
  await api_base.delete(`/api/v1/case/file/remove/${file_id}`);
};


export const toggleCaseArchive = async (id: number): Promise<Case> => {
  const response = await api_base.post(`/api/v1/case/archive/${id}?include_type_object=1`);
  const parsedResponse = caseSchema.parse(response.data);
  return parsedResponse;
};

export interface CabinetProceeding {
  id: string;
  case: number;
  action: string;
  date: number;
  has_time: boolean;
  added_by: { id: string; name: string };
  created_at: string;
  updated_at: string;
}

export const listCabinetProceedings = async (caseId: number): Promise<CabinetProceeding[]> => {
  const response = await api_base.get(`/api/v1/case/${caseId}/cabinet/proceeding/list`);
  return response.data;
};

export const createCabinetProceeding = async (caseId: number, data: Partial<CabinetProceeding>): Promise<CabinetProceeding> => {
  const response = await api_base.post(`/api/v1/case/${caseId}/cabinet/proceeding/create`, data);
  return response.data;
};

export const updateCabinetProceeding = async (id: string, data: Partial<CabinetProceeding>): Promise<CabinetProceeding> => {
  const response = await api_base.put(`/api/v1/case/cabinet/proceeding/update/${id}`, data);
  return response.data;
};

export const removeCabinetProceeding = async (id: string): Promise<void> => {
  await api_base.delete(`/api/v1/case/cabinet/proceeding/remove/${id}`);
};

// Case Relations
export interface CaseRelation {
  id: string;
  created_at: number;
  updated_at: number;
  relation_type: string;
  jurisdiction: string;
  related_case_number: string;
  case: number;
}

interface CaseRelationList {
  next: string | null;
  previous: string | null;
  count: number;
  results: CaseRelation[];
}

export const listCaseRelations = async (caseId: number): Promise<CaseRelationList> => {
  const response = await api_base.get(`/api/v1/case/${caseId}/caseRelations/list`);
  return response.data;
};

// Case Hierarchy Types
export type CaseRelationType = 'appeal' | 'cassation' | 'related' | 'continuation' | 'opposition';

export interface CaseHierarchyItem {
  id: number;
  name: string;
  year: number | null;
  number: number | null;
  code: number | null;
  relation_type: CaseRelationType | null;
  client: { id: string; name: string };
  location: { id: number; name: string } | null;
  created_at: number;
}

export interface CaseHierarchyDescendant {
  case: CaseHierarchyItem;
  children: CaseHierarchyDescendant[];
}

export interface CaseHierarchyResponse {
  current: CaseHierarchyItem;
  ancestors: CaseHierarchyItem[];
  descendants: CaseHierarchyDescendant[];
}

export const getCaseHierarchy = async (caseId: number): Promise<CaseHierarchyResponse> => {
  const response = await api_base.get(`/api/v1/case/${caseId}/hierarchy/`);
  return response.data;
};

export interface CreateRelatedCaseBody {
  name: string;
  client: string;
  parent_case_id: number;
  relation_type: CaseRelationType;
  year?: number | null;
  number?: number | null;
  code?: number | null;
  assigned_to?: string | null;
  location?: number | null;
  service_fee?: string | null;
  fee_type?: 'fixed' | 'percentage' | null;
  fee_reference_amount?: string | null;
  type?: string | null;
  visibility?: 'public' | 'partial' | 'private';
}

export const createRelatedCase = async (body: CreateRelatedCaseBody): Promise<Case> => {
  const response = await api_base.post('/api/v1/case/create?include_type_object', body);
  const parsedResponse = caseSchema.parse(response.data);
  return parsedResponse;
};

// Case Adversaries
export interface AdversaryTableParams {
  page: number;
  page_size: number;
  ordering: string | null;
  search: string | null;
  client_type: string | null;
  id_type: string | null;
}

export interface CaseAdversary {
  id: string;
  client: {
    id: string;
    name: string;
    client_type: 'individual' | 'corporate';
    email: string | null;
    phone: string | null;
    idnumber: string | null;
    id_type: string | null;
    address: string | null;
    city: string | null;
    country: string | null;
    birth_date: string | null;
    nationality: string | null;
    profession: string | null;
    legal_form: string | null;
    rc_number: string | null;
    rc_city: string | null;
    if_number: string | null;
    activity_sector: string | null;
    legal_representative: string | null;
    representative_role: string | null;
    company_creation_date: string | null;
    notes: string | null;
  };
  role?: string;
  role_display?: string;
  added_by: Person;
  created_at: number;
}

interface CaseAdversaryList {
  next: number | null;
  previous: number | null;
  results: CaseAdversary[];
  count: number;
}

export const get_case_adversaries = async (
  caseId: number,
  params: AdversaryTableParams | null = null
): Promise<CaseAdversaryList> => {
  const response = await api_base.get(
    `/api/v1/case/${caseId}/adversary/list`,
    {
      params: { ...params },
      paramsSerializer: params => serializeParams(params)
    }
  );
  return response.data;
};

export const get_adversaries = async (
  params: AdversaryTableParams | null = null
): Promise<CaseAdversaryList> => {
  const response = await api_base.get(
    '/api/v1/adversary/list',
    {
      params: { ...params },
      paramsSerializer: params => serializeParams(params)
    }
  );
  /* 
   * Normalize flat global adversaries (Clients) to nested CaseAdversary structure 
   * so shared components can consume them.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const normalizedResults = response.data.results.map((item: any) => ({
    id: item.id,
    client: item,
    added_by: item.added_by,
    created_at: item.created_at
  }));

  return {
    ...response.data,
    results: normalizedResults
  };
};

export interface AddAdversary {
  name: string;
  client_type?: 'individual' | 'corporate';
  email?: string | null;
  phone?: string | null;
  idnumber?: string | null;
  id_type?: string | null;
  address?: string | null;
  city?: string | null;
  country?: string | null;
  birth_date?: string | null;
  nationality?: string | null;
  profession?: string | null;
  legal_form?: string | null;
  rc_number?: string | null;
  rc_city?: string | null;
  if_number?: string | null;
  activity_sector?: string | null;
  legal_representative?: string | null;
  representative_role?: string | null;
  company_creation_date?: string | null;
  notes?: string | null;
}

export const add_case_adversary = async (caseId: number, body: AddAdversary): Promise<CaseAdversary> => {
  const response = await api_base.post(
    `/api/v1/case/${caseId}/adversary/create`,
    body
  );
  return response.data;
};

export const add_adversary = async (body: AddAdversary): Promise<CaseAdversary> => {
  const response = await api_base.post(
    '/api/v1/adversary/create',
    body
  );
  return response.data;
};

export const update_case_adversary = async (uuid: string, body: Partial<AddAdversary>): Promise<CaseAdversary> => {
  const response = await api_base.patch(
    `/api/v1/case/adversary/update/${uuid}`,
    body
  );
  return response.data;
};


export const remove_case_adversary = async (uuid: string): Promise<void> => {
  await api_base.delete(`/api/v1/case/adversary/remove/${uuid}`);
};

// Party Management for Case Dialog
export const add_case_party = async (caseId: number, body: { client: string; role: 'adversary' | 'client' }): Promise<void> => {
  await api_base.post(`/api/v1/case/${caseId}/party/add`, body);
};

export const create_and_add_case_party = async (caseId: number, body: { new_client: AddAdversary; role: 'adversary' | 'client' }): Promise<void> => {
  await api_base.post(`/api/v1/case/${caseId}/party/add`, body);
};

export const remove_case_party = async (partyId: string): Promise<void> => {
  await api_base.delete(`/api/v1/case/party/remove/${partyId}`);
};

// AI Case Summary
export interface CaseSummaryMetadata {
  summary_id: string;
  generated_at: string;
  completed_at: string | null;
  tokens_used: number;
  credits_consumed: number;
  documents_analyzed: number;
  processing_time_ms: number;
  model_used: string;
  cached: boolean;
  is_stale: boolean;
}

export interface CaseSummaryResponse {
  success: boolean;
  mode: 'quick' | 'deep';
  language: 'ar' | 'fr';
  case_id: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  summary: any;
  metadata: CaseSummaryMetadata;
  error?: string;
}

export interface CaseSummaryHistoryResponse {
  success: boolean;
  case_id: number;
  summaries: Array<{
    id: string;
    mode: 'quick' | 'deep';
    language: 'ar' | 'fr';
    status: string;
    total_tokens: number;
    credits_consumed: number;
    created_at: string;
    completed_at: string | null;
  }>;
}

export const get_case_summary = async (
  caseId: number, 
  mode: 'quick' | 'deep' = 'quick',
  language?: 'ar' | 'fr'
): Promise<CaseSummaryResponse> => {
  const params: Record<string, string> = { mode };
  if (language) params.language = language;
  
  const response = await api_base.get(`/api/v1/case/${caseId}/ai-summary/`, { params });
  return response.data;
};

export const generate_case_summary = async (
  caseId: number,
  mode: 'quick' | 'deep',
  options?: {
    language?: 'ar' | 'fr';
    force_regenerate?: boolean;
    focus_areas?: string[];
  }
): Promise<CaseSummaryResponse> => {
  const response = await api_base.post(`/api/v1/case/${caseId}/ai-summary/`, {
    mode,
    ...options
  });
  return response.data;
};

export const get_case_summary_history = async (caseId: number): Promise<CaseSummaryHistoryResponse> => {
  const response = await api_base.get(`/api/v1/case/${caseId}/ai-summary/history/`);
  return response.data;
};
